import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';

const useVitePostHog = () => {
    return useMemo(() => {
        if (typeof window !== "undefined" &&
            window.posthog &&
            !(Array.isArray(window.posthog) && window.posthog.length === 0)) {
            return window.posthog;
        }
        return null;
    }, []);
};

/**
 * A hook that returns an array of active feature flags.
 * @returns an array of active feature flags
 */
function useActiveFeatureFlags() {
    const client = useVitePostHog();
    const [featureFlags, setFeatureFlags] = useState([]);
    useEffect(() => {
        return client?.onFeatureFlags((flags) => {
            setFeatureFlags(flags);
        });
    }, [client]);
    return featureFlags;
}

/**
 * A hook that returns whether a feature flag is enabled.
 * @param flag - the feature flag to check
 * @returns
 */
function useFeatureFlagEnabled(flag) {
    const client = useVitePostHog();
    const [featureEnabled, setFeatureEnabled] = useState();
    useEffect(() => {
        return client?.onFeatureFlags(() => {
            setFeatureEnabled(client.isFeatureEnabled(flag));
        });
    }, [client, flag]);
    return featureEnabled;
}

/**
 * A hook that returns the payload of a feature flag.
 * @param flag - the feature flag in question
 * @returns
 */
function useFeatureFlagPayload(flag) {
    const client = useVitePostHog();
    const [featureFlagPayload, setFeatureFlagPayload] = useState();
    useEffect(() => {
        return client?.onFeatureFlags(() => {
            setFeatureFlagPayload(client.getFeatureFlagPayload(flag));
        });
    }, [client, flag]);
    return featureFlagPayload;
}

/**
 * A hook that returns the variant key of a feature flag.
 * @param flag  - the feature flag in question
 * @returns
 */
function useFeatureFlagVariantKey(flag) {
    const client = useVitePostHog();
    const [featureFlagVariantKey, setFeatureFlagVariantKey] = useState();
    useEffect(() => {
        return client?.onFeatureFlags(() => {
            setFeatureFlagVariantKey(client.getFeatureFlag(flag));
        });
    }, [client, flag]);
    return featureFlagVariantKey;
}

/**
 * Uses getActiveMatchingSurveys to find the survey with the given id.
 * @param surveyId - id of the survey to find
 * @param eventMetaData - metadata to send with the survey dismissed event
 * @returns
 */
const useGetActiveMatchingSurvey = (surveyId, eventMetaData) => {
    const posthog = useVitePostHog();
    const [surveyState, setSurveyState] = useState({
        currentSurvey: null,
        surveyFound: false,
        isLoading: true,
    });
    useEffect(() => {
        posthog?.getActiveMatchingSurveys((surveys) => {
            if (surveys.length > 0) {
                const targetSurvey = surveys.find((survey) => survey.id === surveyId);
                if (targetSurvey) {
                    setSurveyState({
                        currentSurvey: targetSurvey,
                        surveyFound: true,
                        isLoading: false,
                    });
                }
                else {
                    setSurveyState({
                        currentSurvey: null,
                        surveyFound: false,
                        isLoading: false,
                    });
                }
            }
            else {
                setSurveyState({
                    currentSurvey: null,
                    surveyFound: false,
                    isLoading: false,
                });
            }
        }, true);
    }, [posthog, surveyId]);
    const trackDismiss = () => {
        posthog?.capture("survey dismissed", {
            $survey_id: surveyId,
            ...eventMetaData,
        });
    };
    return useMemo(() => {
        return {
            currentSurvey: surveyState.currentSurvey,
            surveyFound: surveyState.surveyFound,
            isLoading: surveyState.isLoading,
            trackDismiss: trackDismiss,
        };
    }, [surveyState]);
};

function PostHogFeature({ flag, match, children, fallback, visibilityObserverOptions, trackInteraction, trackView, ...props }) {
    const payload = useFeatureFlagPayload(flag);
    const variant = useFeatureFlagVariantKey(flag);
    const shouldTrackInteraction = trackInteraction ?? true;
    const shouldTrackView = trackView ?? true;
    if (match === undefined || variant === match) {
        const childNode = typeof children === "function" ? children(payload) : children;
        return (React.createElement(VisibilityAndClickTracker, { flag: flag, options: visibilityObserverOptions, trackInteraction: shouldTrackInteraction, trackView: shouldTrackView, ...props }, childNode));
    }
    return React.createElement(React.Fragment, null, fallback);
}
function captureFeatureInteraction(flag, posthog) {
    posthog?.capture("$feature_interaction", {
        feature_flag: flag,
        $set: { [`$feature_interaction/${flag}`]: true },
    });
}
function captureFeatureView(flag, posthog) {
    posthog?.capture("$feature_view", { feature_flag: flag });
}
function VisibilityAndClickTracker({ flag, children, trackInteraction, trackView, options, ...props }) {
    const ref = useRef(null);
    const posthog = useVitePostHog();
    const visibilityTrackedRef = useRef(false);
    const clickTrackedRef = useRef(false);
    const cachedOnClick = useCallback(() => {
        if (!clickTrackedRef.current && trackInteraction) {
            captureFeatureInteraction(flag, posthog);
            clickTrackedRef.current = true;
        }
    }, [flag, posthog, trackInteraction]);
    useEffect(() => {
        if (ref.current === null || !trackView)
            return;
        const onIntersect = (entry) => {
            if (!visibilityTrackedRef.current && entry.isIntersecting) {
                captureFeatureView(flag, posthog);
                visibilityTrackedRef.current = true;
            }
        };
        // eslint-disable-next-line compat/compat
        const observer = new IntersectionObserver(([entry]) => onIntersect(entry), {
            threshold: 0.1,
            ...options,
        });
        observer.observe(ref.current);
        return () => observer.disconnect();
    }, [flag, options, posthog, ref, trackView]);
    return (React.createElement("div", { ref: ref, ...props, onClick: cachedOnClick }, children));
}

const PageTrack = ({ uniquePageTitle, children, eventMetaData, }) => {
    const posthog = useVitePostHog();
    useEffect(() => {
        posthog?.capture("$pageview", {
            "Page Title": uniquePageTitle,
            ...eventMetaData,
        });
    }, []);
    return React.createElement(React.Fragment, null, children);
};

export { PageTrack, PostHogFeature, useActiveFeatureFlags, useFeatureFlagEnabled, useFeatureFlagPayload, useFeatureFlagVariantKey, useGetActiveMatchingSurvey, useVitePostHog };
